import React, { ReactNode, useEffect, useState } from "react"
import { useQuery } from "react-query"
import { SearchPreference } from "../../../payment/listing/types"
import AccountLimitService from "../../../state/account-limit/AccountLimitService"
import { ORGANIZATION_TYPE } from "../../../state/contact/services/ContactService"
import CustomerAccount from "../../../state/customer-account/models/CustomerAccount"
import CustomerAccountService from "../../../state/customer-account/service/CustomerAccountService"
import { FILTERS_DEFAULTS, ObjectType, ObjectTypeConfig, PAGINATION_DEFAULTS, SORTING_DEFAULTS, View } from "../../../state/listing/generic-listing"
import { ColumnDefinitionCategory, DataType } from "../../../state/listing/generic-listing/column-definitions"
import SearchService from "../../../state/search/SearchService"
import Pagination from "../pagination.component"
import ContactListingService from "./ContactListingService"
import InvoiceReceivePaymentButton from "./creation-buttons/InvoiceReceivePaymentButton"
import ReturnCreationButton from "./creation-buttons/ReturnCreationButton"
import GenericListingTable from "./listing-table/ListingTable"
import { getDefaultSortId, getDefaultView, getObjectTypeConfig } from "./utils"

interface OwnProps {
    contactId: string
    objectType: ObjectType
    noResultElement?: JSX.Element
}

const GenericContactListing = (props: OwnProps) => {
    const [paginatedSearch, setPaginatedSearch] = useState<SearchPreference>({
        activePage: PAGINATION_DEFAULTS.activePage,
        pageSize: PAGINATION_DEFAULTS.pageSize,
        sortByField: getDefaultSortId(props.objectType),
        sortAsc: SORTING_DEFAULTS.asc,
        keywords: FILTERS_DEFAULTS.keywords,
        filters: FILTERS_DEFAULTS.filters,
        view: getDefaultView(props.objectType),
    } as SearchPreference)
    const controller = new AbortController()
    const [config, setConfig] = useState<ObjectTypeConfig>()
    const [contactId] = useState(props.contactId)
    const [hasNoEmployee, setHasNoEmployee] = useState(false)


    useEffect(() => {
        let config = getObjectTypeConfig(props.objectType)

        config = { ...config }

        // Removing contact columns
        const filteredColumns = config.columnDefinitions.filter(
            (def) => !def.hidden && def.dataType != DataType.CONTACT && !def.categories.includes(ColumnDefinitionCategory.CONTACT_RELATED))
        config.columnDefinitions = filteredColumns

        setConfig(config)

        return () => controller.abort()
    }, [])


    const queryKey = ["contact-listing", paginatedSearch, props.contactId]

    const { data: accountLimits,
        isLoading: isLoadingAccountLimits,
    } = useQuery("account-limit", () => AccountLimitService.Get())

    const { data,
        isLoading: isLoadingCustomerAccount,
    } = useQuery(queryKey, async () => {
        const customerAccount = await CustomerAccountService.getCustomerAccount(props.contactId)
        if (customerAccount instanceof CustomerAccount) {
            if (customerAccount.getContactType() === ORGANIZATION_TYPE) {
                if (customerAccount.getContact().getPrimaryContactId() == "") {
                    setHasNoEmployee(true)
                }
            }
            const contactIds = customerAccount.getCustomerAccountIds()

            const searchData = await ContactListingService.listingSearch(buildSearchPayload(contactIds))
            const searchTotals =
                await ContactListingService.listingSearchTotals(buildSearchPayload(contactIds))

            return { customerAccount, searchData, searchTotals }
        }
    })


    const buildSearchPayload = ((contactIds: string[]) => {
        const totals = SearchService.getTotals(props.objectType)

        return {
            objects: [props.objectType],
            fields: [
                "identifier",
            ],
            filters: {
                contactId: contactIds,
            },
            keywords: "*",
            lightResult: true,
            totals: totals,
            activePage: paginatedSearch.activePage,
            pageSize: paginatedSearch.pageSize,
            sortAsc: paginatedSearch.sortAsc,
            sortByField: paginatedSearch.sortByField,
        }

    })

    const updatePaginatedSearch = ((newState: Partial<SearchPreference>) => {
        setPaginatedSearch((curr) => ({ ...curr, ...newState }))
    })
    const handlePaginationChange = (activePage: number) => {
        updatePaginatedSearch({ activePage })
    }
    const handlePageSizeChange = (pageSize: number) => {
        updatePaginatedSearch({ pageSize })
    }
    const handleApplyView = (view: View) => {
        updatePaginatedSearch({ view })
    }
    const handleSortingChange = (sortField: string, asc: boolean) => {
        updatePaginatedSearch({
            sortByField: sortField,
            sortAsc: asc,
        })
    }

    const getCreationButton = (): ReactNode => {
        const Component = config?.creationButtonComponent

        return Component && !hasNoEmployee
            ? (
                <Component.default accountLimits={accountLimits} contactId={contactId} />
            )
            : (
                <></>
            )
    }

    const getInvoiceButtons = (): ReactNode => {

        const invoiceReceivePaymentButton = data && parseFloat(data.customerAccount.getOwingBalance()) > 0 ?
            <InvoiceReceivePaymentButton contactId={contactId} customerAccount={data.customerAccount} /> : <></>

        const newReturnButton = !hasNoEmployee && <ReturnCreationButton
            contactId={contactId}
            className={"btn-warning"}
        />

        return <>
            <span className="mr-2">
                {invoiceReceivePaymentButton}
            </span><span className="mr-2">
                {newReturnButton}
            </span>
        </>
    }


    const isLoading = isLoadingAccountLimits || isLoadingCustomerAccount

    return (
        <div className="generic-listing">
            {config && (<>
                <GenericListingTable
                    config={config}
                    data={data?.searchData?.data || []}
                    totalItemsCount={data?.searchData?.metadata.totalItemsCount || 0}
                    view={paginatedSearch.view}
                    sorting={{
                        sortField: paginatedSearch.sortByField,
                        asc: paginatedSearch.sortAsc,
                    }}
                    selectableRows={false}
                    additionalOptions={{
                        labelOptions: [],
                    }}
                    loadingState={{
                        loadingData: isLoading,
                    }}
                    onSortingChange={handleSortingChange}
                    onOpenBulkModal={() => null}
                    onApplyView={handleApplyView}
                    totals={data?.searchTotals}
                    disableEdit={true}
                    noResultElement={props.noResultElement}
                />
                <div className="col-12 mb-2 d-flex justify-content-between align-items-center pr-0 mb-4">
                    <div>
                        <Pagination
                            className="px-0"
                            activePage={paginatedSearch.activePage}
                            pageSize={paginatedSearch.pageSize}
                            showPageSize={true}
                            totalItemsCount={data?.searchData?.metadata.totalItemsCount || 0}
                            onChange={handlePaginationChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </div>
                    <div>
                        {props.objectType == ObjectType.INVOICES &&
                            getInvoiceButtons()
                        }
                        {getCreationButton()}
                    </div>
                </div>
            </>)}

        </div>
    )
}
export default GenericContactListing


// WEBPACK FOOTER //
// ./src/common/listing/generic-listing/GenericContactListing.tsx